import { useState, useEffect, useCallback } from 'react'
import Cookies from 'js-cookie'
import type { RegionConfig } from './type.js'

export type UseIPRedirectOptions = {
  /** Cookie 名称,默认为 'user_region' */
  cookieName?: string
  /** Cookie 过期天数,默认为 90 天 */
  cookieExpireDays?: number
  /** 地区配置列表 */
  regions: RegionConfig[]
  /** 当前站点的地区代码（如 'US', 'CN' 等），用于判断是否需要跳转。强烈建议提供此参数以防止无限跳转 */
  currentRegionCode?: string
  /** 可选的地区检测回调函数,返回检测到的地区代码。如不需要自动检测,可不传此参数 */
  onDetectRegion?: () => Promise<string | null>
  /** 是否自动显示弹窗,默认为 true */
  autoShow?: boolean
  /** 白名单列表，这些 URL/路径不会触发 IP 跳转和横幅显示。支持完整 URL（如 'https://www.anker.com/prime-2025'）、路径（如 '/checkout'）和通配符（如 '/checkout/*' 或 'https://www.anker.com/campaign/*'） */
  excludePaths?: string[]
  /** 当前页面路径（用于白名单检查） */
  currentPath?: string
  /** 当前页面完整 URL（用于白名单检查） */
  currentUrl?: string
}

export type UseIPRedirectReturn = {
  /** 是否显示地区选择弹窗 */
  isOpen: boolean
  /** 设置弹窗显示状态 */
  setIsOpen: (open: boolean) => void
  /** 用户已保存的地区代码 */
  savedRegion: string | null
  /** 检测到的用户地区代码 */
  detectedRegion: string | null
  /** 保存用户选择的地区 */
  saveRegion: (regionCode: string, languageCode?: string) => void
  /** 清除已保存的地区选择 */
  clearRegion: () => void
  /** 根据地区代码获取地区配置 */
  getRegionByCode: (code: string) => RegionConfig | undefined
}

/**
 * 检查当前路径是否在白名单中
 * 支持完整 URL 匹配（如 https://www.anker.com/prime-2025）
 * 也支持路径匹配（如 /prime-2025）
 */
function isPathExcluded(currentPath: string, excludePaths: string[], currentUrl?: string): boolean {
  if (!excludePaths || excludePaths.length === 0) {
    return false
  }

  return excludePaths.some(pattern => {
    // 如果 pattern 是完整 URL（以 http:// 或 https:// 开头）
    if (pattern.startsWith('http://') || pattern.startsWith('https://')) {
      if (!currentUrl) return false

      // 支持通配符 * 匹配完整 URL
      if (pattern.includes('*')) {
        const regexPattern = pattern.replace(/\*/g, '.*').replace(/\//g, '\\/')
        const regex = new RegExp(`^${regexPattern}$`)
        return regex.test(currentUrl)
      }
      // 精确匹配完整 URL
      return currentUrl === pattern
    }

    // 否则作为路径匹配
    // 支持通配符 * 匹配
    if (pattern.includes('*')) {
      const regexPattern = pattern.replace(/\*/g, '.*')
      const regex = new RegExp(`^${regexPattern}$`)
      return regex.test(currentPath)
    }
    // 精确匹配
    return currentPath === pattern
  })
}

/**
 * 地区选择 Hook
 * 用于管理用户地区选择和 Cookie 存储
 */
export function useIPRedirect(options: UseIPRedirectOptions): UseIPRedirectReturn {
  const {
    cookieName = 'user_region',
    cookieExpireDays = 90,
    regions,
    currentRegionCode,
    onDetectRegion,
    autoShow = true,
    excludePaths = [],
    currentPath = '',
    currentUrl = '',
  } = options

  const [isOpen, setIsOpen] = useState(false)
  const [savedRegion, setSavedRegion] = useState<string | null>(null)
  const [detectedRegion, setDetectedRegion] = useState<string | null>(null)

  // 从 Cookie 读取已保存的地区
  useEffect(() => {
    const saved = Cookies.get(cookieName)
    if (saved) {
      setSavedRegion(saved)
    }
  }, [cookieName])

  // 保存用户选择的地区
  const saveRegion = useCallback(
    (regionCode: string, languageCode?: string) => {
      // 格式：v1_国家_语言（如 v1_CA_EN）
      // 如果没有提供 languageCode，使用 regionCode（向后兼容）
      const cookieValue = languageCode ? `v1_${regionCode}_${languageCode}` : `v1_${regionCode}`
      Cookies.set(cookieName, cookieValue, { expires: cookieExpireDays })
      setSavedRegion(regionCode)
    },
    [cookieName, cookieExpireDays]
  )

  // 检测用户地区
  useEffect(() => {
    // 白名单检查：如果当前 URL/路径在白名单中，不执行任何检测和跳转
    if (isPathExcluded(currentPath, excludePaths, currentUrl)) {
      return
    }

    // 检查 URL 参数，如果存在 _region_selected 标记，清理掉并标记本次会话已跳转
    const urlParams = new URLSearchParams(window.location.search)
    const wasRedirected = urlParams.get('_region_selected')

    if (wasRedirected) {
      // 清理 URL 参数，但不保存 Cookie
      // 使用 sessionStorage 记录本次会话已跳转，避免刷新后再次跳转
      sessionStorage.setItem(`${cookieName}_redirected`, 'true')
      urlParams.delete('_region_selected')
      const newUrl =
        window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash
      window.history.replaceState({}, '', newUrl)
    }

    // 如果已有保存的地区选择，不再检测
    if (savedRegion) {
      return
    }

    // 如果本次会话已经跳转过，不再检测
    if (sessionStorage.getItem(`${cookieName}_redirected`)) {
      return
    }

    // 执行 IP 检测
    if (onDetectRegion) {
      onDetectRegion().then(detected => {
        if (detected) {
          setDetectedRegion(detected)

          // 关键判断：只有当检测到的地区与当前站点不同时才显示弹窗
          if (currentRegionCode) {
            if (detected !== currentRegionCode) {
              if (autoShow) {
                setIsOpen(true)
              }
            }
            // 🔧 如果检测到的地区与当前站点匹配，不保存 Cookie
            // Cookie 只在以下情况保存：
            // 1. 用户从其他站点跳转过来（URL 带 _region_selected 参数）
            // 2. 用户点击 "Stay Here" 按钮手动保存
          } else {
            // 如果没有提供 currentRegionCode，使用旧的行为（总是显示弹窗）
            if (autoShow) {
              setIsOpen(true)
            }
          }
        }
      })
    }
  }, [
    onDetectRegion,
    savedRegion,
    autoShow,
    currentRegionCode,
    saveRegion,
    cookieName,
    currentPath,
    excludePaths,
    currentUrl,
  ])

  // 清除已保存的地区选择
  const clearRegion = () => {
    Cookies.remove(cookieName)
    setSavedRegion(null)
  }

  // 根据地区代码获取地区配置
  const getRegionByCode = (code: string) => {
    return regions.find(region => region.code === code)
  }

  return {
    isOpen,
    setIsOpen,
    savedRegion,
    detectedRegion,
    saveRegion,
    clearRegion,
    getRegionByCode,
  }
}
